<div class="container mx-auto px-4 py-8 max-w-4xl">
  <!-- Header -->
  <div class="mb-8">
    <h1 class="text-4xl font-bold text-gray-800 dark:text-white mb-4">
      Do Not Sell My Personal Information
    </h1>
    <p class="text-sm text-gray-500 dark:text-gray-400">
      Last Updated: {{ lastUpdated }}
    </p>
  </div>

  <!-- Main Content -->
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 space-y-6">
    <!-- Introduction -->
    <section>
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        Your California Privacy Rights
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-4">
        The California Consumer Privacy Act (CCPA) provides California residents with specific rights regarding their personal information. This page explains your right to opt-out of the sale of your personal information.
      </p>
    </section>

    <!-- What We Collect -->
    <section>
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        Information We Collect
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-3">
        {{ companyName }} collects the following categories of personal information:
      </p>
      <ul class="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
        <li><strong>Identifiers:</strong> Name, email address, account credentials</li>
        <li><strong>Commercial Information:</strong> Subscription history, watchlists, stock preferences</li>
        <li><strong>Internet Activity:</strong> Browsing history on our platform, search queries, interactions with our service</li>
        <li><strong>Geolocation Data:</strong> General location information based on IP address</li>
        <li><strong>Professional Information:</strong> Investment preferences and experience level (if provided)</li>
      </ul>
    </section>

    <!-- Do We Sell -->
    <section class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
      <h2 class="text-2xl font-semibold text-blue-900 dark:text-blue-100 mb-4">
        <i class="pi pi-info-circle mr-2"></i>Important Notice
      </h2>
      <p class="text-blue-800 dark:text-blue-200 text-lg font-medium mb-3">
        {{ companyName }} does NOT sell your personal information.
      </p>
      <p class="text-blue-800 dark:text-blue-200">
        We do not and will not sell your personal information to third parties for monetary or other valuable consideration. We may share certain information with service providers who help us operate our platform, but these are not considered "sales" under CCPA.
      </p>
    </section>

    <!-- Service Providers -->
    <section>
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        Service Providers We Work With
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-3">
        We share information with the following types of service providers to operate our business:
      </p>
      <ul class="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
        <li><strong>Authentication Services:</strong> Auth0 for secure login and identity management</li>
        <li><strong>Payment Processing:</strong> Stripe for subscription billing and payments</li>
        <li><strong>Email Services:</strong> Brevo (formerly Sendinblue) for transactional and marketing emails</li>
        <li><strong>Cloud Infrastructure:</strong> Microsoft Azure for hosting and data storage</li>
        <li><strong>Analytics:</strong> Usage analytics to improve our service</li>
      </ul>
      <p class="text-gray-700 dark:text-gray-300 mt-4">
        These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
      </p>
    </section>

    <!-- Your Rights -->
    <section>
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        Your CCPA Rights
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-3">
        As a California resident, you have the following rights:
      </p>
      <div class="space-y-4">
        <div class="border-l-4 border-green-500 pl-4">
          <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Right to Know</h3>
          <p class="text-gray-700 dark:text-gray-300 text-sm">
            Request disclosure of the personal information we collect, use, and share about you.
          </p>
        </div>
        <div class="border-l-4 border-green-500 pl-4">
          <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Right to Delete</h3>
          <p class="text-gray-700 dark:text-gray-300 text-sm">
            Request deletion of your personal information, subject to certain exceptions.
          </p>
        </div>
        <div class="border-l-4 border-green-500 pl-4">
          <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Right to Opt-Out</h3>
          <p class="text-gray-700 dark:text-gray-300 text-sm">
            Opt-out of the sale of your personal information (though we don't sell your data).
          </p>
        </div>
        <div class="border-l-4 border-green-500 pl-4">
          <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Right to Non-Discrimination</h3>
          <p class="text-gray-700 dark:text-gray-300 text-sm">
            Exercise your privacy rights without receiving discriminatory treatment.
          </p>
        </div>
      </div>
    </section>

    <!-- How to Exercise Rights -->
    <section>
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        How to Exercise Your Rights
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-4">
        To exercise any of your CCPA rights, you can:
      </p>
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 space-y-4">
        <div class="flex items-start">
          <i class="pi pi-download text-green-600 dark:text-green-400 text-xl mr-3 mt-1"></i>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Request Data Export</h3>
            <p class="text-gray-700 dark:text-gray-300 text-sm">
              Log in to your account and visit the <a href="/profile/data-export" class="text-green-600 dark:text-green-400 hover:underline">Data Export</a> page to download your information.
            </p>
          </div>
        </div>
        <div class="flex items-start">
          <i class="pi pi-trash text-red-600 dark:text-red-400 text-xl mr-3 mt-1"></i>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Request Account Deletion</h3>
            <p class="text-gray-700 dark:text-gray-300 text-sm">
              Log in to your account and visit the <a href="/profile/delete-account" class="text-green-600 dark:text-green-400 hover:underline">Delete Account</a> page to request deletion.
            </p>
          </div>
        </div>
        <div class="flex items-start">
          <i class="pi pi-envelope text-blue-600 dark:text-blue-400 text-xl mr-3 mt-1"></i>
          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white mb-1">Contact Us</h3>
            <p class="text-gray-700 dark:text-gray-300 text-sm">
              Email us at <a href="mailto:{{ contactEmail }}" class="text-green-600 dark:text-green-400 hover:underline">{{ contactEmail }}</a> with your request.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Verification -->
    <section>
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        Verification Process
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-3">
        To protect your privacy and security, we will verify your identity before processing requests to know or delete your personal information. The verification process may include:
      </p>
      <ul class="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
        <li>Confirming your email address matches our records</li>
        <li>Verifying account credentials through secure login</li>
        <li>Requesting additional information to confirm your identity</li>
      </ul>
    </section>

    <!-- Contact Information -->
    <section class="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-white mb-4">
        Contact Information
      </h2>
      <p class="text-gray-700 dark:text-gray-300 mb-3">
        If you have questions about this notice or our privacy practices:
      </p>
      <div class="space-y-2 text-gray-700 dark:text-gray-300">
        <p><strong>Company:</strong> {{ companyName }}</p>
        <p><strong>Address:</strong> {{ companyAddress }}</p>
        <p><strong>Email:</strong> <a href="mailto:{{ contactEmail }}" class="text-green-600 dark:text-green-400 hover:underline">{{ contactEmail }}</a></p>
      </div>
    </section>

    <!-- Related Links -->
    <section class="border-t border-gray-200 dark:border-gray-700 pt-6">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
        Related Privacy Information
      </h2>
      <div class="flex flex-wrap gap-4">
        <a href="/privacy-policy" class="text-green-600 dark:text-green-400 hover:underline">
          <i class="pi pi-file-pdf mr-1"></i>Privacy Policy
        </a>
        <a href="/terms-of-service" class="text-green-600 dark:text-green-400 hover:underline">
          <i class="pi pi-file-pdf mr-1"></i>Terms of Service
        </a>
        <a href="/profile/data-export" class="text-green-600 dark:text-green-400 hover:underline">
          <i class="pi pi-download mr-1"></i>Export Your Data
        </a>
        <a href="/profile/delete-account" class="text-green-600 dark:text-green-400 hover:underline">
          <i class="pi pi-trash mr-1"></i>Delete Your Account
        </a>
      </div>
    </section>
  </div>
</div>
